import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiShield, FiSearch, FiHome, FiLogIn } = FiIcons;

const WhistleblowerLogin = () => {
  const navigate = useNavigate();
  const { companies } = useData();
  const [companySearch, setCompanySearch] = useState('');

  // Filter companies based on search
  const filteredCompanies = companies.filter(c => 
    c.active && c.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  const handleCompanySelect = (companyId) => {
    // VIKTIG FIKS: Endret fra /public/ til /s/ for å matche App.jsx ruter
    navigate(`/s/${companyId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Navigation Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors">
              <SafeIcon icon={FiHome} />
              <span>Tilbake til forsiden</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiShield} className="text-white text-sm" />
              </div>
              <span className="font-semibold text-gray-900">HMSvarsling</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiShield} className="text-white text-2xl" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Varsler-portal</h1>
            <p className="text-gray-600">Finn din bedrift for å sende inn en ny varsling.</p>
          </div>

          {/* Return User Login */}
          <div className="mb-6 p-4 bg-white rounded-xl shadow-lg border border-gray-200 text-center">
            <button 
              onClick={() => navigate('/whistleblower/return-login')}
              className="w-full flex items-center justify-center space-x-2 text-blue-600 font-semibold hover:text-blue-800 transition-colors py-3 rounded-lg hover:bg-blue-50"
            >
              <SafeIcon icon={FiLogIn} className="text-xl"/>
              <span>Har du en sak fra før? Logg inn her</span>
            </button>
          </div>

          {/* Company Selection */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Velg bedrift for å starte en ny varsling *
              </label>
              
              {/* Search Field */}
              <div className="relative mb-4">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Søk etter bedrift..." 
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Company List */}
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredCompanies.length > 0 ? (
                  filteredCompanies.map(company => (
                    <motion.div 
                      key={company.id}
                      whileHover={{ backgroundColor: '#f9fafb' }}
                      className="p-6 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50"
                      onClick={() => handleCompanySelect(company.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          {company.branding?.logo ? (
                            <img src={company.branding.logo} alt={`${company.name} logo`} className="h-12 w-auto" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: company.branding?.primaryColor || '#2563EB' }}>
                              <SafeIcon icon={FiShield} className="text-white text-xl" />
                            </div>
                          )}
                          <div>
                            <h3 className="font-medium text-gray-900 text-lg">{company.name}</h3>
                            <p className="text-sm text-gray-500">
                              {company.industry} • {company.employees} ansatte
                            </p>
                            {company.branding?.welcomeMessage && (
                              <p className="text-sm text-blue-600 mt-1">{company.branding.welcomeMessage}</p>
                            )}
                          </div>
                        </div>
                        <SafeIcon icon={FiShield} className="text-gray-400 text-xl" />
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    {companySearch ? 'Ingen bedrifter funnet' : 'Begynn å skrive for å søke'}
                  </div>
                )}
              </div>
            </div>

            {/* Info Section */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-medium text-blue-900 mb-3">Hvorfor velge din bedrift?</h3>
              <div className="space-y-2 text-sm text-blue-800">
                <p>• Hver bedrift har sine egne retningslinjer og prosedyrer</p>
                <p>• Du får tilpasset informasjon og veiledning</p>
                <p>• Direkte kontakt med riktig saksbehandler</p>
                <p>• Trygg og sikker behandling i henhold til bedriftens rutiner</p>
              </div>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>
              Vi følger GDPR og ivaretar ditt personvern. Les mer om våre{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline">personvernregler</Link>.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WhistleblowerLogin;